// Global watcher for incoming customer self-orders. Mounted once in the app
// shell so it fires on EVERY staff screen (Dashboard, Order Taking, Setup…).
// Polls a lightweight endpoint; when a new pending self-order arrives it
// plays a chime and forces a big confirm/reject modal.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { playChime } from '../utils/chimes';
import './SelfOrderWatcher.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const POLL_MS = 2000;

export default function SelfOrderWatcher() {
  const { token } = useAuth();
  const [queue, setQueue] = useState([]);   // pending self-orders awaiting action
  const [busy, setBusy] = useState(false);
  const seenRef = useRef(new Set());

  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/orders/pending-self`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const orders = await res.json();
      const stillPending = new Set(orders.map(o => o.id));
      const fresh = orders.filter(o => !seenRef.current.has(o.id));
      fresh.forEach(o => seenRef.current.add(o.id));

      setQueue(prev => {
        // Drop any that are no longer pending (confirmed/rejected elsewhere).
        const kept = prev.filter(o => stillPending.has(o.id));
        const merged = [...kept];
        for (const f of fresh) if (!merged.some(o => o.id === f.id)) merged.push(f);
        return merged;
      });

      if (fresh.length > 0) {
        playChime('self-order');
        if ('Notification' in window && Notification.permission === 'granted') {
          const o = fresh[0];
          try {
            new Notification('New customer order', {
              body: `${o.table_number ? `Table ${o.table_number}` : 'Walk-in'} · ${(o.items || []).length} item(s) · रू ${Number(o.total || 0).toFixed(0)}`,
            });
          } catch (_) {}
        }
      }
    } catch (_) { /* transient */ }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') { try { await poll(); } catch (_) {} }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(tick, 400);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    timer = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [poll, token]);

  const current = queue[0];
  if (!current) return null;

  async function act(status, reason) {
    setBusy(true);
    try {
      const body = { status };
      if (reason) body.cancel_reason = reason;
      const res = await fetch(`${API_BASE}/orders/${current.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Could not update the order');
      }
      setQueue(prev => prev.filter(o => o.id !== current.id));
      // Tell any open order list (Dashboard "Today's Orders") to refetch NOW,
      // so a confirmed self-order jumps from Self-order → Taken immediately
      // instead of waiting for the dashboard's next ~15s poll.
      try { window.dispatchEvent(new CustomEvent('vyapar:orders-refresh')); } catch (_) {}
      // Pull any further pending orders straight away so the next one
      // surfaces instantly instead of waiting for the timer.
      poll();
    } catch (e) { alert(e.message); }
    setBusy(false);
  }

  const itemCount = (current.items || []).length;
  const queued = queue.length;
  // Pending self-orders are stored with total=0 (it's computed on confirm),
  // so fall back to summing the line items for display.
  const lineSum = (current.items || []).reduce(
    (s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0), 0
  );
  const displayTotal = Number(current.total) > 0 ? Number(current.total) : lineSum;

  // Dismiss the current order without acting on it — it stays pending and can
  // be handled later from Order Taking. Won't re-pop this session (already seen).
  const dismiss = () => setQueue(prev => prev.filter(o => o.id !== current.id));

  return (
    <div className="sow-overlay" role="dialog" aria-modal="true">
      <div className="sow-card">
        <button
          type="button"
          className="sow-close"
          aria-label="Dismiss for now"
          title="Dismiss — handle later from Order Taking"
          disabled={busy}
          onClick={dismiss}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="sow-head">
          <div className="sow-eye">New Customer Order</div>
          <div className="sow-title">
            {current.table_number ? `Table ${current.table_number}` : 'Walk-in'}
          </div>
          <div className="sow-sub">
            Order {current.order_number || `#${current.id}`} · {itemCount} item{itemCount !== 1 ? 's' : ''}
            {queued > 1 ? ` · ${queued - 1} more waiting` : ''}
          </div>
        </div>

        <div className="sow-items">
          {(current.items || []).map(it => (
            <div key={it.id} className="sow-row">
              <span className="sow-qty">×{it.quantity}</span>
              <span className="sow-name">{it.name}</span>
              <span className="sow-line">रू {((it.unit_price || 0) * it.quantity).toFixed(0)}</span>
            </div>
          ))}
        </div>

        <div className="sow-total">
          <span>Total</span>
          <span className="sow-total-num">रू {displayTotal.toFixed(0)}</span>
        </div>

        <div className="sow-actions">
          <button
            type="button"
            className="sow-reject"
            disabled={busy}
            onClick={() => {
              const reason = window.prompt('Reject this order — reason for the customer?', 'Out of stock');
              if (reason === null) return;
              act('cancelled', reason || 'Rejected by staff');
            }}
          >
            Reject
          </button>
          <button
            type="button"
            className="sow-confirm"
            disabled={busy}
            onClick={() => act('taken')}
          >
            {busy ? 'Working…' : '✓ Confirm — send to kitchen'}
          </button>
        </div>
      </div>
    </div>
  );
}
